import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import CZTooltip from '../CZTooltip/CZTooltip';
import { shipment } from '../../../models/entities/shipment/shipment';
import { PagesRoutes } from '../../../routing/PagesRoutes';
import { Link } from 'react-router-dom';
import airConsolIcon from '../../../static/icons/shipmentTypes/AIR_CONSOL.svg';
import oceanConsolIcon from '../../../static/icons/shipmentTypes/OCEAN_CONSOL.svg';
import {
    getConsolidationTitleSuffix,
    isAirOrOceanConsolidationGroup
} from '../../pages/shipments/consolidations/consolidationUtils';

export type ConsolidationTagKind = 'air' | 'ocean';

type ShipmentMasterFields = Pick<shipment, 'typeCode'> & { master?: string | null };

/**
 * Same visibility rules as the consolidations list:
 * master set, group size > 1, and Air (AE/AI) or Ocean (OI/OE/OEX).
 */
export function getConsolidationTagKind(
    currentShipment: ShipmentMasterFields,
    allShipments: ShipmentMasterFields[]
): ConsolidationTagKind | null {
    const master = currentShipment.master?.trim();
    if (!master) return null;

    const group = allShipments.filter((s) => s.master?.trim() === master) as shipment[];
    if (!isAirOrOceanConsolidationGroup(group)) return null;

    const kind = getConsolidationTitleSuffix(group);
    if (kind === 'Air') return 'air';
    if (kind === 'Ocean') return 'ocean';
    return null;
}

type Props = {
    shipment: ShipmentMasterFields;
    allShipments: ShipmentMasterFields[];
    fetchShipments?: () => void;
    iconWidth?: number;
};

const ConsolidationTagWithTooltip: FC<Props> = ({ shipment, allShipments, fetchShipments, iconWidth = 34 }) => {
    const master = shipment.master?.trim();
    const candidateType =
        ['AE', 'AI'].includes(shipment.typeCode) || ['OI', 'OE', 'OEX'].includes(shipment.typeCode);

    useEffect(() => {
        if (master && candidateType && fetchShipments) {
            fetchShipments();
        }
    }, [master, candidateType, fetchShipments]);

    const kind = getConsolidationTagKind(shipment, allShipments);
    if (!kind || !master) return null;

    const label = kind === 'air' ? 'Air consolidation' : 'Ocean consolidation';
    const src = kind === 'air' ? airConsolIcon : oceanConsolIcon;
    const to = `${PagesRoutes.ShipmentsConsolidations}/${encodeURIComponent(master)}`;

    return (
        <CZTooltip text={label}>
            <TagLink to={to} aria-label={label}>
                <TagIcon width={iconWidth} src={src} alt={label} />
            </TagLink>
        </CZTooltip>
    );
};

const TagLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    line-height: 0;
    margin-right: 6px;
`;

const TagIcon = styled.img<{ width: number }>`
    display: block;
    width: ${(p) => p.width}px;
    height: ${(p) => p.width}px;
`;

export default ConsolidationTagWithTooltip;
